import { useMemo } from 'react';

import shuffle from 'lodash/shuffle';

import {
	localStorageData,
	queryParam,
	useRegisterMetadata,
} from '../hooks/use-registered-metadata';
import {
	USER_SEGMENTATION_TEAM_TYPE_MARKETING,
	USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
	UserSegmentationTeamType,
	wacToIdentityUserSegMap,
} from '../models/user-segmentation';
import {
	getTeamTypeToCheckboxMap,
	getAgenticSoftwareCheckboxVals,
	checkboxValues,
} from '../views/jtbd/constants';

export const getDefaultValueFromTeamType = (teamType: UserSegmentationTeamType) => {
	const map = getTeamTypeToCheckboxMap();
	return map[teamType]?.defaultValues;
};

export const mapJTBDtoTemplateChoice = (
	checkboxes: checkboxValues[],
	teamType: string | undefined,
) => {
	const checkedBoxes = checkboxes.filter((checkbox) => checkbox.isChecked);

	const teamTypeDefaultValues = teamType
		? getDefaultValueFromTeamType(teamType as UserSegmentationTeamType)
		: undefined;

	if (checkedBoxes.length === 0 && teamTypeDefaultValues) {
		return teamTypeDefaultValues;
	}
	const sortedCheckboxes = checkedBoxes.sort((a, b) => a.priority - b.priority);

	return {
		template: sortedCheckboxes[0]?.template,
		persona: sortedCheckboxes[0]?.persona,
	};
};
/**
 * Returns current URL with query parameters added
 *
 * @param params {object} - query parameters to add
 *
 * @returns {string} - URL with query parameters
 */
export function getCurrentUrlWithParams(params: { [key: string]: string | string[] }) {
	const url = new URL(window.location.href);
	Object.entries(params).forEach(([param, value]) => {
		if (Array.isArray(value)) {
			const existingValues = url.searchParams.get(param)?.split(',') || [];
			const newValues = [...existingValues, ...value];
			url.searchParams.set(param, newValues.join(','));
		} else {
			url.searchParams.set(param, value);
		}
	});
	return url.toString();
}

export const getJtbdTemplateOverrideQueryParam = () => {
	return new URLSearchParams(window.location.search).get('jtbdTemplateOverride') ?? undefined;
};

export const TemplateQueryParamScrum = 'scrum';

export const ELIGIBLE_TEAM_TYPES_FOR_JTBD_SCREEN: UserSegmentationTeamType[] = [
	USER_SEGMENTATION_TEAM_TYPE_MARKETING,
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
	USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
];

export const useEligibleTeamTypeForJtbdLocalStorage = () => {
	const metadata = useRegisterMetadata();
	const foundTeamType = ELIGIBLE_TEAM_TYPES_FOR_JTBD_SCREEN.find(
		(teamType) => metadata?.get(localStorageData.team) === wacToIdentityUserSegMap[teamType],
	);

	return foundTeamType;
};

export const useGetTeamTypeData = (showAgenticVariant = false) => {
	const metadata = useRegisterMetadata();
	const teamType = useMemo(() => {
		// Reconcile team between query params and local storage
		const foundTeamType = ELIGIBLE_TEAM_TYPES_FOR_JTBD_SCREEN.find(
			(teamType) =>
				metadata?.get(queryParam.teamType) === teamType ||
				metadata?.get(localStorageData.team) === wacToIdentityUserSegMap[teamType],
		);

		return foundTeamType ?? undefined;
	}, [metadata]);

	const isSoftwareTeamType = teamType === USER_SEGMENTATION_TEAM_TYPE_SOFTWARE;

	const teamTypeToCheckboxMap = useMemo(() => getTeamTypeToCheckboxMap(), []);

	if (isSoftwareTeamType) {
		// When the agentic variant is active, use the agentic checkbox values.
		// getAgenticSoftwareCheckboxVals() returns fresh copies each call,
		// avoiding mutation of the shared module-level constant.
		const sourceCheckboxes = showAgenticVariant
			? getAgenticSoftwareCheckboxVals()
			: teamTypeToCheckboxMap[teamType]?.checkboxValues?.map((cb) => ({
					...cb,
				}));
		const newTeamTypeCheckboxes = shuffle(sourceCheckboxes);
		return {
			teamType,
			teamTypeCheckboxes: newTeamTypeCheckboxes,
			isSoftwareTeamType,
		};
	}

	// Non-software team types: return fresh copies to avoid mutation
	const teamTypeCheckboxes = teamType
		? teamTypeToCheckboxMap[teamType]?.checkboxValues?.map((cb) => ({
				...cb,
			}))
		: undefined;

	return { teamType, teamTypeCheckboxes };
};

export const isEligibleTeamTypeToViewJtbdScreen = (teamType: UserSegmentationTeamType) => {
	const teamTypesSkippingJtbd = [
		USER_SEGMENTATION_TEAM_TYPE_MARKETING,
		USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
	];

	if (!ELIGIBLE_TEAM_TYPES_FOR_JTBD_SCREEN.includes(teamType)) {
		return false;
	}

	// Skip the JTBD screen for marketing and operations team types.
	if (teamTypesSkippingJtbd.includes(teamType)) {
		return true;
	}

	return true;
};
